/**
 * sl-omtr/sl-yphr.31.5: Schema-driven validation, tooltips, and section rendering
 *
 * The UStVA schema JSON is the SINGLE SOURCE OF TRUTH for:
 * - Form section structure (derived from pageMapping)
 * - Field validation (type, signed, maxDigits)
 * - Tooltips (full XSD labels)
 *
 * SOLID:
 * - S: Schema fetching + derived data (sections, validation, tooltips)
 * - O: New schema year = new JSON file, zero code changes
 * - D: Depends on backend REST API abstraction
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { workspaceApiFetch } from '../../cloud';

// ─── Types ──────────────────────────────────────────────────────────────────

interface SchemaField {
  name: string;
  type: 'integer' | 'decimal' | 'flag' | 'date' | 'enum' | 'string' | 'year';
  signed: boolean;
  required: boolean;
  label: string;
  maxDigits?: number;
  exactDecimalPlaces?: number;
  pageMapping?: {
    section: number;
    sectionTitle: string;
    column: 'bmg' | 'steuer' | 'full';
    zeile: number;
  };
}

export interface UstvaSchemaData {
  version: string;
  fields: Record<string, SchemaField>;
}

export interface KzValidationResult {
  valid: boolean;
  error: string | null;
}

// ─── Section Types (replaces ustva-sections.ts) ─────────────────────────────

export type FieldInputType =
  | 'euro'
  | 'euro-cent'
  | 'checkbox'
  | 'date'
  | 'select'
  | 'text'
  | 'readonly';

export interface UStVAField {
  kz: number;
  label: string;
  tooltip?: string;
  inputType: FieldInputType;
  column: 'bmg' | 'steuer' | 'full';
  computed?: boolean;
  zeile?: number;
}

export interface UStVAFieldGroup {
  title: string;
  twoColumns: boolean;
  fields: UStVAField[];
}

export interface UStVASection {
  number: number;
  title: string;
  tooltip?: string;
  groups: UStVAFieldGroup[];
}

/** Kz83 is auto-calculated (Zahllast) — domain knowledge not in XSD */
const COMPUTED_KZ = new Set([83]);

// ─── Schema Hook ────────────────────────────────────────────────────────────

/** Fetch and cache the UStVA schema from backend */
export function useUstvaSchema(workspaceId: string): UstvaSchemaData | null {
  const [schema, setSchema] = useState<UstvaSchemaData | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const versionsRes = await workspaceApiFetch(
          `/api/workspace/${workspaceId}/elster/schema/versions`,
          { credentials: 'include' }
        );
        if (!versionsRes.ok || cancelled) return;
        const { versions } = (await versionsRes.json()) as {
          versions: string[];
        };
        if (!versions.length || cancelled) return;

        const latestYear = versions[versions.length - 1];
        const schemaRes = await workspaceApiFetch(
          `/api/workspace/${workspaceId}/elster/schema?year=${latestYear}`,
          { credentials: 'include' }
        );
        if (!schemaRes.ok || cancelled) return;
        const data = (await schemaRes.json()) as UstvaSchemaData;
        if (!cancelled) setSchema(data);
      } catch {
        // Intentionally ignore network/schema load errors in hook.
      }
    };
    run().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  return schema;
}

// ─── Validation Hook ────────────────────────────────────────────────────────

/** Validate a Kz field value against schema constraints */
export function useKzFieldValidation(
  schema: UstvaSchemaData | null
): (kz: number, rawValue: string) => KzValidationResult {
  return useCallback(
    (kz: number, rawValue: string): KzValidationResult => {
      if (!schema) return { valid: true, error: null };
      if (!rawValue || rawValue.trim() === '')
        return { valid: true, error: null };

      const field = schema.fields[`Kz${kz}`];
      if (!field) return { valid: true, error: null };

      // Clean input: remove thousands separators, normalize decimal separator
      const cleaned = rawValue.replace(/\./g, '').replace(',', '.');

      if (field.type === 'integer') {
        if (!/^-?\d+$/.test(cleaned)) {
          return { valid: false, error: 'Nur ganze Zahlen erlaubt' };
        }
        if (!field.signed && cleaned.startsWith('-')) {
          return { valid: false, error: 'Keine negativen Werte erlaubt' };
        }
        if (field.maxDigits) {
          const digits = cleaned.replace('-', '');
          if (digits.length > field.maxDigits) {
            return {
              valid: false,
              error: `Maximal ${field.maxDigits} Stellen`,
            };
          }
        }
      }

      if (field.type === 'decimal') {
        if (!/^-?\d+([.]\d+)?$/.test(cleaned)) {
          return { valid: false, error: 'Ungültiges Dezimalformat' };
        }
        if (!field.signed && cleaned.startsWith('-')) {
          return { valid: false, error: 'Keine negativen Werte erlaubt' };
        }
        if (field.exactDecimalPlaces) {
          const parts = cleaned.split('.');
          if (parts[1] && parts[1].length > field.exactDecimalPlaces) {
            return {
              valid: false,
              error: `Maximal ${field.exactDecimalPlaces} Nachkommastellen`,
            };
          }
        }
      }

      if (field.type === 'flag' && cleaned !== '0' && cleaned !== '1') {
        return { valid: false, error: 'Nur 0 oder 1 erlaubt' };
      }

      return { valid: true, error: null };
    },
    [schema]
  );
}

// ─── Tooltip Hook ───────────────────────────────────────────────────────────

/** Get the full XSD label for a Kz field (for tooltips) */
export function useKzTooltips(
  schema: UstvaSchemaData | null
): Map<number, string> {
  return useMemo(() => {
    const map = new Map<number, string>();
    if (!schema) return map;

    for (const [name, field] of Object.entries(schema.fields)) {
      const kzMatch = name.match(/^Kz(\d+)$/);
      if (kzMatch) {
        map.set(parseInt(kzMatch[1]), field.label);
      }
    }

    return map;
  }, [schema]);
}

// ─── Schema-Driven Section Builder ──────────────────────────────────────────

/** Derive inputType from XSD field type */
function deriveInputType(field: SchemaField): FieldInputType {
  if (field.type === 'flag') return 'checkbox';
  if (field.type === 'decimal') return 'euro-cent';
  if (field.type === 'date') return 'date';
  if (field.type === 'enum') return 'select';
  if (field.type === 'string') return 'text';
  return 'euro'; // integer → euro (whole euros)
}

/**
 * Build UStVASection[] from schema pageMapping — SINGLE SOURCE OF TRUTH.
 *
 * Groups fields by section number, sorts by zeile, derives twoColumns
 * from presence of bmg/steuer columns.
 */
function buildSectionsFromSchema(schema: UstvaSchemaData): UStVASection[] {
  // 1. Group fields by section
  const sectionMap = new Map<
    number,
    { title: string; fields: SchemaField[] }
  >();

  for (const field of Object.values(schema.fields)) {
    if (!field.pageMapping) continue;
    const { section, sectionTitle } = field.pageMapping;
    if (!sectionMap.has(section)) {
      sectionMap.set(section, { title: sectionTitle, fields: [] });
    }
    sectionMap.get(section)!.fields.push(field);
  }

  // 2. Build sections sorted by section number
  const sections: UStVASection[] = [];

  for (const [sectionNum, { title, fields }] of [...sectionMap.entries()].sort(
    (a, b) => a[0] - b[0]
  )) {
    // Sort fields by zeile
    fields.sort((a, b) => a.pageMapping!.zeile - b.pageMapping!.zeile);

    // Derive twoColumns: true if any field uses bmg or steuer
    const hasBmgOrSteuer = fields.some(
      f => f.pageMapping!.column === 'bmg' || f.pageMapping!.column === 'steuer'
    );

    const ustvaFields: UStVAField[] = fields.map(f => {
      const kzNum = parseInt(f.name.replace('Kz', ''));
      const isComputed = COMPUTED_KZ.has(kzNum);
      return {
        kz: kzNum,
        label: f.label,
        tooltip: f.label,
        inputType: isComputed
          ? ('readonly' as FieldInputType)
          : deriveInputType(f),
        column: f.pageMapping!.column,
        computed: isComputed || undefined,
        zeile: f.pageMapping!.zeile,
      };
    });

    sections.push({
      number: sectionNum,
      title,
      groups: [
        {
          title: '',
          twoColumns: hasBmgOrSteuer,
          fields: ustvaFields,
        },
      ],
    });
  }

  return sections;
}

/**
 * Hook: Build UStVA sections from schema.
 * Returns empty array while schema is loading — callers must handle loading state.
 */
export function useUstvaSections(
  schema: UstvaSchemaData | null
): UStVASection[] {
  return useMemo(() => {
    if (!schema) return [];
    return buildSectionsFromSchema(schema);
  }, [schema]);
}

// ─── Schema-Derived Helpers (replace ustva-sections.ts helpers) ─────────────

/** Get all Kz numbers from schema sections */
export function getAllSectionKzCodes(sections: UStVASection[]): Set<number> {
  const codes = new Set<number>();
  for (const section of sections) {
    for (const group of section.groups) {
      for (const field of group.fields) {
        codes.add(field.kz);
      }
    }
  }
  return codes;
}

/** Find field definition by Kz code */
export function findFieldByKz(
  sections: UStVASection[],
  kz: number
): UStVAField | undefined {
  for (const section of sections) {
    for (const group of section.groups) {
      for (const field of group.fields) {
        if (field.kz === kz) return field;
      }
    }
  }
  return undefined;
}

/** Check if a Kz code expects decimal (Euro,Cent) input */
export function isDecimalField(sections: UStVASection[], kz: number): boolean {
  const field = findFieldByKz(sections, kz);
  return field?.inputType === 'euro-cent';
}
